/* eslint-disable react-refresh/only-export-components */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Switch, useLocation } from 'react-router-dom';
import { Loader } from '@els/els-react--loader';
import Page from '@Containers/Page';
import ConnectedRoute from '@AppRoutes/ConnectedRoute';
import { paths } from '@AppRoutes/constants.js';
import withReducer from '@Hooks/withReducer';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import { setPageTitle } from '@Services/PageTitle';
import ConditionsBanner from './components/ConditionsBanner';
import ConditionsBreadcrumbs from './components/ConditionsBreadcrumbs';
import ConditionsNavigatorContainer from './ConditionsNavigatorContainer';
import ConditionsDetailContainer from './ConditionsDetailContainer';
import ConditionsDraw from './ConditionsDraw';
import { conditionsReducerKey, CONDITIONS_PAGE_TITLE } from './constants';
import conditionsReducer from './reducer';
import './Conditions.scss';

const CONDITIONS_REFLOW_BODY_CLASS = 'conditions-reflow';

function getProjectNameFromLocation(locationState = {}) {
  return locationState?.projectName || locationState?.referenceLabel || '';
}

function Conditions() {
  const location = useLocation();
  // The banner sits above the router outlet, but its "Learn how it works" panel offers example
  // reactions that load into the draw page's editor. Owning the ref here is what lets those two
  // siblings talk; only ConditionsDraw ever populates it.
  const editorInstanceRef = useRef(null);
  const [projectName, setProjectName] = useState(() => getProjectNameFromLocation(location.state));
  const [blockingNavigationCount, setBlockingNavigationCount] = useState(0);
  const isBlockingNavigation = blockingNavigationCount > 0;

  useEffect(() => {
    document.body.classList.add(CONDITIONS_REFLOW_BODY_CLASS);

    return () => document.body.classList.remove(CONDITIONS_REFLOW_BODY_CLASS);
  }, []);

  const handleProjectLoaded = useCallback((loadedProjectName) => {
    setProjectName(loadedProjectName);
    setPageTitle({ pageName: CONDITIONS_PAGE_TITLE, title: loadedProjectName });
  }, []);

  const setBlockingNavigation = useCallback((isBusy) => {
    const updateBlockingNavigationCount = () => {
      setBlockingNavigationCount((count) => (isBusy ? count + 1 : Math.max(0, count - 1)));
    };

    if (isBusy) {
      flushSync(updateBlockingNavigationCount);
      return;
    }

    updateBlockingNavigationCount();
  }, []);

  useEffect(() => {
    // On detail routes the loaded project owns the breadcrumb label via onProjectLoaded — including
    // when the project is served from the Redux cache. Re-seeding from navigation here would clobber
    // that label on cached revisits, because the child's onProjectLoaded effect runs before this
    // parent effect in the same commit. So only seed/clear the label for the list and draw routes.
    const isDetailRoute = location.pathname !== paths.CONDITIONS && location.pathname !== paths.CONDITIONS_DRAW;
    if (isDetailRoute) return;
    setProjectName(getProjectNameFromLocation(location.state));
  }, [location.pathname, location.state]);

  // ConnectedRoute updates the tracking path name in the store on each navigation;
  // fire the page-view tracking afterwards so every Conditions route (list/draw/detail)
  // emits its configured page name, including intra-feature navigation.
  useEffect(() => {
    RouteChangeTracking.trackOnRouteChangeSuccess();
  }, [location.pathname]);

  return (
    <Page withFooter={false} pageClassName="conditions-root">
      {location.pathname === paths.CONDITIONS_DRAW && (
        // A first-time user is redirected straight here from an empty project list, so they never
        // saw the navigator's explanatory copy. Open the panel for them; anyone who reached Draw by
        // choice gets it closed.
        <ConditionsBanner editorInstanceRef={editorInstanceRef} isLearnPanelInitiallyOpen={Boolean(location.state?.noProjects)} />
      )}
      <ConditionsBreadcrumbs projectName={projectName} />
      <Loader isVisible={isBlockingNavigation} isBlocking />
      <Switch>
        <ConnectedRoute exact path={paths.CONDITIONS_DRAW} render={() => <ConditionsDraw editorInstanceRef={editorInstanceRef} />} />
        <ConnectedRoute
          exact
          path={paths.CONDITIONS}
          render={() => <ConditionsNavigatorContainer setBlockingNavigation={setBlockingNavigation} />}
        />
        <ConnectedRoute
          path={`${paths.CONDITIONS}/:id`}
          render={(props) => (
            <ConditionsDetailContainer {...props} onProjectLoaded={handleProjectLoaded} setBlockingNavigation={setBlockingNavigation} />
          )}
        />
      </Switch>
    </Page>
  );
}

export default withReducer(conditionsReducerKey, conditionsReducer, true)(Conditions);
